// Bulk coupon assignment — pick a promotion, upload a CSV of member IDs.
//
// Used TWICE, by two routes that differ only in which promotion type they offer
// and what they call themselves:
//
//   Multiple Members   — promotionType 2 (Member offer),      process BPC
//   Multiple Consumer  — promotionType 1 (Public promotion),  process BPP
//
// Parameterised rather than copied: two files would mean every future coupon fix
// made twice. The process code must differ per type, or each page adopts the
// other's job on refresh and shows its counters as its own.
//
// The job lifecycle (202 + jobid, polling, resuming a running job on arrival,
// attaching to a refused duplicate, and when the error file may be offered)
// lives in useBulkJob, shared with the other two bulk pages; the output panels
// come from BulkJobStatus. This page owns only its promotion picker, its chrome
// and its payload.
import React, { useEffect, useState, useContext } from "react";
import { Grid, Box } from '@mui/material';
import { Paper, Button, Typography, MenuItem, TextField } from '@mui/material';
import { useUrlContext } from '../context/urlContext';
import axios from "axios";
import { errorContext } from "../Components/ErrorDisplay";
import { useAuth } from '../context/authContext';
import { useHistory } from "react-router-dom";
import useBulkJob from '../Components/useBulkJob';
import BulkJobStatus from '../Components/BulkJobStatus';

export default function PromotionMemberMap({
    promotionType,
    heading,
    processCode,
}) {
    const { UrlHelperObj } = useUrlContext();
    const { user } = useAuth();
    const history = useHistory();

    const [selectedPromotion, setSelectedPromotion] = useState(null);
    const [promotionList, setPromotionList] = useState([]);
    const [csvFile, setCsvFile] = useState(null);
    // Whether the coupon list actually loaded. An empty list after a FAILED
    // fetch is not "this club has none" — saying so contradicts the error toast
    // and sends people looking for coupons that may well exist.
    const [couponsFetched, setCouponsFetched] = useState(false);
    const { handleError } = useContext(errorContext);

    const csvErrorMessage = "The CSV file contains errors or has an invalid format. Please download the error file and review the errors.";

    const job = useBulkJob({
        processCode,
        fallbackErrorMessage: csvErrorMessage,
    });
    const { submitting, submitJob, resetJob } = job;

    useEffect(() => {
        getPromotionList(user.issuerId);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    useEffect(() => {
        if (promotionList.length > 0 && selectedPromotion === null) {
            setSelectedPromotion(promotionList[0].id);
        }
    }, [promotionList, selectedPromotion]);

    // `submitting` covers both the upload and the whole polling run, so a second
    // import cannot be fired while one is in flight — including one adopted on
    // arrival that this tab never started.
    const isSubmitEnabled = selectedPromotion !== null && csvFile instanceof File && !submitting;

    async function getPromotionList(id) {
        await axios.get(UrlHelperObj.promotionUrl_GET(id, 1))
            .then(response => response.data)
            .then(results => {
                // One type per page: Member offer (2) for membership coupons,
                // Public promotion (1) for consumer coupon passes. Membership
                // promotions (0) are not coupons and never appear here.
                const filtered = Array.isArray(results)
                    ? results.filter(p => Number(p.type) === Number(promotionType))
                    : [];
                setPromotionList(filtered);
                setCouponsFetched(true);
            })
            .catch(err => {
                // Leave couponsFetched false so the page does not claim the club
                // has no coupons when we simply could not ask. handleError shows
                // what actually went wrong.
                setCouponsFetched(false);
                handleError(err);
            });
    }

    function handleChangeInPromotion(e) {
        setSelectedPromotion(e.target.value);
        resetJob();
    }

    function handleChangeInFile(e) {
        resetJob();
        const file = e.target.files[0];
        setCsvFile(file || null);
    }

    async function handleSubmit() {
        const data = new FormData();
        data.append("issuer_id", user.issuerId);
        data.append("promoid", selectedPromotion);
        data.append("image", csvFile);

        const chosenName = csvFile.name;
        // Clearing the file is what stops a second click re-issuing the same
        // coupons; the name lives on in the job state.
        setCsvFile(null);

        await submitJob(UrlHelperObj.promotionMemberUrl(), data, chosenName);

        const fileInput = document.getElementById("csv-upload");
        if (fileInput) fileInput.value = "";
    }

    return (
        <Grid container xs={12} spacing={0} style={{ borderStyle: 'none', borderColor: 'blue', justifyContent: 'flex-start', marginLeft: 20 }}>
            <Grid container item xs={12} style={{ textAlign: 'left', borderStyle: 'none', borderColor: 'greeen', alignItems: 'center', justifyContent: 'space-between' }}>
                <Grid item xs={12} sm={7} md={6}>
                    <Paper style={{ padding: '8px 12px' }}>
                        <Typography variant="h5" style={{ textAlign: 'left' }}>{heading}</Typography>
                    </Paper>
                </Grid>
            </Grid>

            <Grid container xs={12} direction="column" sx={{ mb: 1, borderStyle: 'none', borderColor: 'red', alignItems: 'flex-start' }}>
                <Grid item style={{ width: '100%', maxWidth: '360px', textAlign: 'left', marginTop: '20px', borderStyle: 'none', borderColor: 'green', mb: 2, padding: '10px', borderRadius: '5px', backgroundColor: '#f0f8ff' }}>
                    {/* Locked while a job runs: the promotion is already baked
                        into the running import, so changing it here would only
                        mislead. */}
                    <TextField
                        fullWidth
                        variant='outlined'
                        select
                        id='selectedPromotion'
                        name='selectedPromotion'
                        value={selectedPromotion ?? ""}
                        label="Select Coupon"
                        onChange={handleChangeInPromotion}
                        disabled={submitting}
                    >
                        {promotionList && promotionList.map(type => (
                            <MenuItem value={type.id} key={type.id}>{type.name}</MenuItem>
                        ))}
                    </TextField>
                    {/* Only once the list has actually loaded — otherwise a failed
                        fetch would be reported as "none exist". */}
                    {couponsFetched && promotionList.length === 0 ? (
                        <Typography
                            sx={{
                                mt: 1,
                                fontSize: '1rem',
                                fontWeight: 600,
                                color: 'warning.dark',
                            }}
                        >
                            No coupons available.
                        </Typography>
                    ) : null}
                </Grid>
            </Grid>

            <Grid container xs={12} direction="column" sx={{ mb: 1, borderStyle: 'none', borderColor: 'red', alignItems: 'flex-start' }}>
                <Grid item style={{ textAlign: 'left', marginTop: '20px', width: '100%', maxWidth: '700px', borderStyle: 'none', borderColor: 'green', mb: 2, padding: '10px', borderRadius: '5px', backgroundColor: '#f0f8ff' }}>
                    <Typography>Please select CSV file containing member IDs</Typography>
                </Grid>
            </Grid>

            <Grid container xs={12} direction="column" sx={{ mb: 0, borderStyle: 'none', borderColor: 'red', alignItems: 'flex-start' }}>
                <Grid item style={{ textAlign: 'left', marginTop: '10px', width: '100%', maxWidth: '700px', borderStyle: 'none', borderColor: 'green', mb: 0, padding: '10px', borderRadius: '5px', backgroundColor: '#f0f8ff' }}>
                    {/* Disabled while a job is in flight. The `disabled` on the
                        INPUT is what actually stops it: a label click on a
                        disabled control does not open the picker. The one on the
                        Button is for the greyed-out look. */}
                    <input
                        id="csv-upload"
                        type="file"
                        accept=".csv"
                        onChange={handleChangeInFile}
                        disabled={submitting}
                        style={{ display: 'none' }}
                    />
                    <Box sx={{ borderStyle: 'none', borderColor: 'green', display: 'flex', gap: 2, alignItems: 'center' }}>
                        <Box sx={{ flex: 1, gap: 1, maxWidth: 300 }}>
                            <Typography
                                sx={{
                                    border: '1px dashed #ccc',
                                    borderRadius: 1,
                                    p: 1,
                                    flex: 1,
                                    minHeight: 40,
                                    display: 'flex',
                                    alignItems: 'center',
                                    justifyContent: 'flex-start',
                                    textAlign: 'left',
                                }}
                                color="text.secondary"
                            >
                                {/* After submit csvFile is null, so fall back to
                                    the name the job reports — which is also how a
                                    resumed job names its file. */}
                                {csvFile ? csvFile.name : (job.submittedFileName || 'No file selected')}
                            </Typography>
                        </Box>

                        <label htmlFor="csv-upload">
                            <Button
                                variant="contained"
                                color="primary"
                                component="span"
                                size="small"
                                disabled={submitting}
                                sx={{ minWidth: 96, maxWidth: 96, minHeight: 36, px: 1, py: 0.5, whiteSpace: 'normal', lineHeight: 1.15, textAlign: 'center', textTransform: 'none', borderRadius: 1, bgcolor: 'primary.main', color: 'primary.contrastText', '&:hover': { bgcolor: 'primary.dark' } }}
                            >
                                Choose CSV File
                            </Button>
                        </label>
                    </Box>
                </Grid>

                <BulkJobStatus {...job} onDownloadErrorFile={job.downloadErrorFile} />
            </Grid>

            <Grid container xs={12} direction="column" sx={{ mb: 0, borderStyle: 'none', borderColor: 'red', alignItems: 'flex-start' }}>
                <Grid item style={{ textAlign: 'left', marginTop: '90px', width: '100%', maxWidth: '700px', borderStyle: 'none', borderColor: 'green', mb: 0, padding: '10px', borderRadius: '5px' }}>
                    <Button
                        type="submit"
                        disabled={!isSubmitEnabled}
                        variant="contained"
                        onClick={handleSubmit}
                        color="primary"
                        size="medium"
                        sx={{
                            mt: 0,
                            ml: 1,
                            '&.Mui-disabled': {
                                backgroundColor: '#9e9e9e !important',
                                color: '#424242 !important',
                                opacity: 1,
                                boxShadow: 'none',
                                cursor: 'not-allowed'
                            }
                        }}
                    >
                        {submitting ? (job.isPolling ? 'Processing…' : 'Submitting…') : 'Submit'}
                    </Button>
                    {/* "Back", not "Cancel": this only navigates — it has never
                        stopped anything, and during a running import "Cancel"
                        would read as "abort this import". */}
                    <Button
                        type="button"
                        variant="contained"
                        color="secondary"
                        onClick={() => history.push(user?.issuerId ? `/club/${user.issuerId}/home` : "/")}
                        size="medium"
                        sx={{ mt: 0, ml: 1 }}
                    >
                        Back
                    </Button>
                </Grid>
            </Grid>
        </Grid>
    );
}
